import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { Alert } from "../../components/ui/Alert";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Modal } from "../../components/ui/Modal";
import { PageHeader } from "../../components/ui/PageHeader";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { type Column, Table } from "../../components/ui/Table";
import { ApiError } from "../../lib/api";
import { type EnrollmentBatch, enrollmentApi } from "../../lib/api/enrollment";
import { formatDateTime } from "../../lib/format";
import { t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

import { CreateBatchForm } from "./CreateBatchForm";
import { BootstrapTokensPanel } from "./bootstrap/BootstrapTokensPanel";

type EnrollmentTab = "batches" | "bootstrap";

interface TabOption {
  id: EnrollmentTab;
  label: string;
}

interface TabBarProps {
  tab: EnrollmentTab;
  onChange: (next: EnrollmentTab) => void;
  tabs: TabOption[];
}

function TabBar({ tab, onChange, tabs }: TabBarProps) {
  return (
    <div
      role="tablist"
      style={{
        display: "flex",
        gap: 4,
        borderBottom: "1px solid var(--border, #e2e2e2)",
        marginBottom: 4,
      }}
    >
      {tabs.map((option) => {
        const active = option.id === tab;
        return (
          <button
            key={option.id}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(option.id)}
            style={{
              background: "transparent",
              border: "none",
              padding: "10px 14px",
              fontSize: 14,
              cursor: "pointer",
              borderBottom: active
                ? "2px solid var(--accent, #2563eb)"
                : "2px solid transparent",
              color: active ? "var(--text, #111)" : "var(--text-muted, #666)",
              fontWeight: active ? 600 : 500,
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

export function EnrollmentListPage() {
  useLocale();
  const qc = useQueryClient();
  const [tab, setTab] = useState<EnrollmentTab>("batches");
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<EnrollmentBatch | null>(null);

  const list = useQuery({
    queryKey: ["enrollment", "batches"],
    queryFn: enrollmentApi.listBatches,
  });

  const revoke = useMutation({
    mutationFn: (id: string) => enrollmentApi.revokeBatch(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["enrollment", "batches"] });
      setRevokeTarget(null);
    },
  });

  const columns: Column<EnrollmentBatch>[] = [
    {
      key: "label",
      header: t("enrollment.list.column.label"),
      render: (row) => (
        <Link to={`/enrollment/batches/${row.id}`}>{row.label || row.method}</Link>
      ),
    },
    {
      key: "method",
      header: t("enrollment.list.column.method"),
      render: (row) => <Badge tone="info">{t(`enrollment.method.${row.method}`)}</Badge>,
    },
    {
      key: "max_uses",
      header: t("enrollment.list.column.maxUses"),
      render: (row) => (row.max_uses == null ? "∞" : row.max_uses),
      align: "right",
    },
    {
      key: "tokens",
      header: t("enrollment.list.column.tokens"),
      render: (row) => <span className="num">{row.tokens_total ?? 0}</span>,
      align: "right",
    },
    {
      key: "expires_at",
      header: t("enrollment.list.column.expiresAt"),
      render: (row) => formatDateTime(row.expires_at),
    },
    {
      key: "status",
      header: t("enrollment.list.column.status"),
      render: (row) =>
        row.is_revoked ? (
          <Badge tone="danger">{t("enrollment.status.revoked")}</Badge>
        ) : (
          <Badge tone="success">{t("enrollment.status.active")}</Badge>
        ),
    },
    {
      key: "created_at",
      header: t("enrollment.list.column.createdAt"),
      render: (row) => formatDateTime(row.created_at),
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Link to={`/enrollment/batches/${row.id}`}>
            <Button variant="secondary" size="sm">
              {t("enrollment.list.action.view")}
            </Button>
          </Link>
          {!row.is_revoked && (
            <Button variant="danger" size="sm" onClick={() => setRevokeTarget(row)}>
              {t("enrollment.list.action.revoke")}
            </Button>
          )}
        </div>
      ),
      align: "right",
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t("enrollment.list.eyebrow")}
        title={t("enrollment.list.title")}
        subtitle={t("enrollment.list.subtitle")}
        actions={
          tab === "batches" ? (
            <Button onClick={() => setCreateOpen(true)}>
              {t("enrollment.list.action.create")}
            </Button>
          ) : null
        }
      />

      <TabBar
        tab={tab}
        onChange={setTab}
        tabs={[
          { id: "batches", label: t("enrollment.list.tabs.batches") },
          { id: "bootstrap", label: t("enrollment.list.tabs.bootstrap") },
        ]}
      />

      {tab === "bootstrap" && (
        <div style={{ marginTop: 16 }}>
          <BootstrapTokensPanel />
        </div>
      )}

      {tab === "batches" && (
      <Card padding="none">
        {list.isLoading && <LoadingState />}
        {list.error && (
          <ErrorState
            titleKey="states.errorTitle"
            message={translateError(list.error)}
            action={
              <Button variant="secondary" size="sm" onClick={() => void list.refetch()}>
                {t("common.retry")}
              </Button>
            }
          />
        )}
        {list.data && (
          <Table
            rows={list.data}
            columns={columns}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                titleKey="enrollment.list.emptyTitle"
                bodyKey="enrollment.list.emptyBody"
                action={
                  <Button onClick={() => setCreateOpen(true)}>
                    {t("enrollment.list.action.create")}
                  </Button>
                }
              />
            }
          />
        )}
      </Card>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t("enrollment.create.title")}
      >
        <CreateBatchForm
          onCancel={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            void qc.invalidateQueries({ queryKey: ["enrollment", "batches"] });
          }}
        />
      </Modal>

      <Modal
        open={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        title={t("enrollment.revoke.title")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRevokeTarget(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="danger"
              loading={revoke.isPending}
              onClick={() => revokeTarget && revoke.mutate(revokeTarget.id)}
            >
              {t("enrollment.revoke.confirm")}
            </Button>
          </>
        }
      >
        <p>{t("enrollment.revoke.body")}</p>
        {revokeTarget && (
          <div className="code-block" style={{ marginTop: 12 }}>
            {revokeTarget.label || revokeTarget.method}
          </div>
        )}
        {revoke.error && (
          <div style={{ marginTop: 12 }}>
            <Alert tone="danger">{translateError(revoke.error)}</Alert>
          </div>
        )}
      </Modal>
    </div>
  );
}
